import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { SignInPage } from './pages/SignInPage';
import { JobsPage } from './pages/JobsPage';
import { JobOverviewPage } from './pages/JobOverviewPage';
import { JobInfoPage } from './pages/JobInfoPage';
import { GroupMembersPage } from './pages/GroupMembersPage';
import { SubjectQRCardPage } from './pages/SubjectQRCardPage';

// The import flow pulls in pdf.js + jsQR (a few hundred kB). Lazy-loading
// it keeps that weight off the hot, offline, on-set path (jobs, search,
// QR card) that photographers use over and over on-site.
const ImportPage = lazy(() => import('./pages/ImportPage').then(m => ({ default: m.ImportPage })));
const ImportReviewPage = lazy(() => import('./pages/ImportReviewPage').then(m => ({ default: m.ImportReviewPage })));

// Admin-only screens — never needed by a photographer's device, so kept
// out of the main bundle too.
const AdminPage = lazy(() => import('./pages/AdminPage').then(m => ({ default: m.AdminPage })));
const AssignPhotographersPage = lazy(() =>
  import('./pages/AssignPhotographersPage').then(m => ({ default: m.AssignPhotographersPage }))
);

function LoadingScreen() {
  return <div className="screen screen--centered">Loading…</div>;
}

function RequireAuth({ children }: { children: React.ReactElement }) {
  const { session, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!session) return <Navigate to="/signin" replace />;
  return children;
}

function RequireAdmin({ children }: { children: React.ReactElement }) {
  const { session, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!session) return <Navigate to="/signin" replace />;
  if (session.role !== 'admin') return <Navigate to="/jobs" replace />;
  return children;
}

export function AppRouter() {
  return (
    <Routes>
      <Route path="/signin" element={<SignInPage />} />
      <Route path="/jobs" element={<RequireAuth><JobsPage /></RequireAuth>} />
      <Route
        path="/admin"
        element={
          <RequireAdmin>
            <Suspense fallback={<LoadingScreen />}>
              <AdminPage />
            </Suspense>
          </RequireAdmin>
        }
      />
      <Route
        path="/jobs/:jobId/assign"
        element={
          <RequireAdmin>
            <Suspense fallback={<LoadingScreen />}>
              <AssignPhotographersPage />
            </Suspense>
          </RequireAdmin>
        }
      />
      <Route
        path="/import"
        element={
          <RequireAuth>
            <Suspense fallback={<div className="screen screen--centered">Loading importer…</div>}>
              <ImportPage />
            </Suspense>
          </RequireAuth>
        }
      />
      <Route
        path="/import/review/:jobId"
        element={
          <RequireAuth>
            <Suspense fallback={<div className="screen screen--centered">Loading importer…</div>}>
              <ImportReviewPage />
            </Suspense>
          </RequireAuth>
        }
      />
      <Route path="/jobs/:jobId" element={<RequireAuth><JobOverviewPage /></RequireAuth>} />
      <Route path="/jobs/:jobId/info" element={<RequireAuth><JobInfoPage /></RequireAuth>} />
      <Route path="/jobs/:jobId/groups/:groupId" element={<RequireAuth><GroupMembersPage /></RequireAuth>} />
      <Route path="/jobs/:jobId/subjects/:subjectId" element={<RequireAuth><SubjectQRCardPage /></RequireAuth>} />
      <Route path="*" element={<Navigate to="/jobs" replace />} />
    </Routes>
  );
}
